import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "tss-react/mui";

import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AddIcon from "@mui/icons-material/Add";
import AltRouteIcon from "@mui/icons-material/AltRoute";

import { ButtonUI, ConfirmPopup } from "../../Common";
import { showToast } from "../../../store/actions";
import WorkspaceClass from "../../../service/workspace";
import AddLocation from "./AddLocation";
import S3FileBrowser from "../Browsers/S3FileBrowser";
import SSHFileBrowser from "../Browsers/SSHFileBrowser";
import AzureFileBrowser from "../Browsers/AzureFileBrowser";
import CommonLayout from "../../Common/Layout";
import { regexName } from "../../../util/regularExpress";
import SharePointBrowser from "../Browsers/SharePointBrowser";
import KMKTable from "../../Common/Table/KMKTable";
import HelpContextIcon from "../../Common/HelpPanel/HelpContextIcon";
import useWindowResize from "../../../hooks/useWindowsReszie";

const dataProviderList = [
  { value: "S3", text: "AWS S3" },
  { value: "Salesforce", text: "Salesforce" },
  { value: "sftp", text: "SFTP" },
  { value: "azure", text: "Azure Blob" },
  { value: "sharepoint", text: "SharePoint" },
  { value: "mysql", text: "Aurora MySQL" },
  { value: "veevavault", text: "Veeva Vault" },
  { value: "snowflake", text: "Snowflake" },
];

const useStyles = makeStyles()((theme) => ({
  Header: {
    fontSize: "1.1rem",
    backgroundColor: "#e8edf1",
    color: "black",
    marginTop: 0,
    borderRadius: 0,
    boxShadow: 0,
    fontWeight: 600,
    padding: "25px 25px 0 25px",
    display: "flex",
    flexWrap: "wrap",
  },
  HeaderTitle: {
    fontSize: 20,
    lineHeight: 1.5,
    paddingTop: 4,
    display: "inline-flex",
  },
  PanelContainer: {
    padding: "25px",
    paddingBottom: "0",
    display: "flex",
    flexDirection: "column",
    maxWidth: "100%",
    minWidth: "100%",
    width: "100%",
    minHeight: "250px",
    marginLeft: "inherit",
  },
  DataTableContainer: {
    borderRadius: 10,
    backgroundColor: "#FFFFFF",
    padding: "5px 0px 0px",
    overflowX: "auto",
    "& .MuiTableRow-head .MuiTableCell-root": {
      padding: "6px 16px",
      height: "37px",
    },
    "& .MuiTableRow-head .MuiTableCell-root:last-child": {
      paddingRight: "40px",
    },
    "& .MuiTableBody-root .MuiTableCell-root": {
      padding: "6px 16px",
      borderBottom: "none",
    },
    "& .MuiTableBody-root .MuiTableCell-root>.MuiIconButton-root:first-child": {
      marginLeft: "-10px",
      verticalAlign: "sub",
    },
    "& ::-webkit-scrollbar-track": {
      marginTop: "36px",
    },
  },
  Panel: {
    borderRadius: 10,
    marginBottom: 0,
  },
  listItem: {
    display: "inline-flex",
    color: "rgba(0, 0, 0, 0.87)",
    padding: "0px",
    textTransform: "capitalize",
    justifyContent: "flex-start",
    "& .MuiTypography-root": {
      fontSize: "0.875rem",
    },
    "&.MuiListItemButton-root": {
      padding: "0px !important",
    },
    "&.MuiListItemButton-root:hover": {
      textDecoration: "underline",
      color: "#ed6c02",
      backgroundColor: "transparent",
    },
  },
  IconHolder: {
    padding: 0,
    margin: "0 2px",
    fontSize: "22px",
    height: "32px",
    width: "32px",
    borderRadius: "64px",
  },
}));

const EXPLORER_NAVIGATION = {
  salesforce: "salesforce-explorer",
  veevavault: "veevavault-explorer",
  snowflake: "snowflake-explorer",
};

const DataLocation = () => {
  const workspace = useSelector((state) => state.workspace);
  const { Name: workspaceName, accessRole } = workspace.workspace;

  const [tableLoading, setTableLoading] = useState(false);
  const [loadingForConfirm, setLoadingForConfirm] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [locationList, setLocationList] = useState([]);
  const [openLocationPopup, setOpenLocationPopup] = useState(false);
  const [openFileBrowser, setOpenFileBrowser] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState("");
  const [confirmMessage, setConfirmMessage] = useState("");
  const [locationRowNo, setLocationRowNo] = useState(0);
  const [location, setLocation] = useState("");
  const [selectedConnection, setSelectedConnection] = useState({
    stage: 0,
    url: "/",
    type: "",
    name: "",
    hasUploadPermission: false,
  });

  const { classes } = useStyles();
  const dispatch = useDispatch();
  const { height: tableHeight } = useWindowResize(200);
  const { Workspace_id } = useParams();

  const navigate = useNavigate();

  const handleCancel = () => {
    setOpenConfirm(false);
  };

  const handleEditClick = useCallback(
    (rowNo) => {
      const Stage_id = locationList[rowNo]?.Stage_id || 0;
      if (Stage_id) {
        setSelectedConnection((prev) => ({
          stage: Stage_id,
          url: "/",
          type: "",
          name: prev.name,
        }));

        setOpenLocationPopup(true);
      }
    },
    [locationList]
  );

  const handleDeleteClick = useCallback(
    (rowNo) => {
      setOpenConfirm(true);
      setLocationRowNo(rowNo);
      setConfirmTitle("Delete Connection");
      setConfirmMessage(`Are you sure you want to delete “${locationList[rowNo]?.Name}”?`);
    },
    [locationList]
  );

  const LocationColumns = useMemo(
    () => [
      {
        id: "Name",
        name: "NAME",
        style: { maxWidth: 310, wordBreak: "break-word" },
        width: 120,
        align: "left",
        sortable: false,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => (
          <Box display={"flex"} alignItems={"center"}>
            {record.Detail.type.toLowerCase() === "mysql" ? (
              <p style={{ color: "#1976d2", marginBottom: 0 }}>{record?.Name}</p>
            ) : (
              <Button
                color="info"
                className={classes.listItem}
                style={{ color: "#1976d2" }}
                onClick={() => {
                  const type = record.Detail.type.toLowerCase();

                  setSelectedConnection({
                    name: record.Name,
                    url: record?.Detail?.url || "/",
                    stage: record.Stage_id,
                    hasUploadPermission: record?.Detail?.hasUploadPermission || false,
                    type,
                    ...(type === "azure"
                      ? { container: record.Detail.container }
                      : type === "sharepoint"
                      ? { docuList: record.Detail.docuList }
                      : {}),
                  });

                  if (type === "s3" || type === "sftp" || type === "azure" || type === "sharepoint")
                    setOpenFileBrowser(true);
                  else navigate(`${record.Stage_id}/${EXPLORER_NAVIGATION[type]}`);
                }}
              >
                {record?.Name}
                {record?.IsDataLake === 1 &&
                  (record?.Detail.type.toLowerCase() === "s3" ? (
                    <img src="/images/aws-s3-logo.png" alt="S3" style={{ width: "20px", marginLeft: "10px" }} />
                  ) : (
                    <img src="/images/azure-logo.png" alt="Azure" style={{ width: "20px", marginLeft: "10px" }} />
                  ))}
              </Button>
            )}
            {record.Detail.type.toLowerCase() === "s3" && record?.Detail?.hasOwnProperty("hasUploadPermission") && (
              <Tooltip
                placement="right"
                title={record?.Detail?.hasUploadPermission ? "File → S3 Bucket" : "File → Control Panel → S3 Bucket"}
              >
                <AltRouteIcon
                  sx={{
                    height: "22px ",
                    width: "22px  ",
                    marginLeft: "7px",
                    cursor: "pointer",
                  }}
                />
              </Tooltip>
            )}
          </Box>
        ),
      },
      {
        id: "PROVIDER",
        name: "PROVIDER",
        style: { maxWidth: 290, wordBreak: "break-word" },
        width: 100,
        align: "left",
        sortable: false,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => {
          let selectedProviderObj = dataProviderList.filter(
            (provider) => record?.Detail?.type.toLowerCase() === provider.value.toLowerCase()
          );

          return selectedProviderObj[0].text || "-";
        },
      },
      {
        id: "CONNECTION",
        name: "CONNECTION",
        style: { maxWidth: "400px", wordBreak: "break-all" },
        width: 150,
        align: "left",
        sortable: false,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) =>
          record.Detail.port ? `${record.Detail.url}:${record.Detail.port}` : record?.Detail?.url,
      },
      {
        id: "ACTIONS",
        name: "ACTIONS",
        style: { maxWidth: 110, wordBreak: "break-word" },
        width: 120,
        align: "right",
        sortable: false,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => {
          return (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              {!record?.IsDataLake && (
                <Tooltip title="Edit">
                  <IconButton className={classes.IconHolder} onClick={() => handleEditClick(index)}>
                    <EditOutlinedIcon color="primary" fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              )}
              {[1, 2].includes(accessRole) ? (
                <Tooltip title="Delete">
                  <IconButton className={classes.IconHolder} onClick={() => handleDeleteClick(index)}>
                    <DeleteOutlineOutlinedIcon color="error" fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              ) : (
                <></>
              )}
            </div>
          );
        },
      },
    ],
    [accessRole, classes.IconHolder, classes.listItem, handleDeleteClick, handleEditClick, navigate]
  );

  const loadLocationList = useCallback(async () => {
    setTableLoading(true);
    const workspace = new WorkspaceClass();
    const result = await workspace.getLocationList(Workspace_id);

    if (result?.length) setLocationList(result);

    setTableLoading(false);
  }, [Workspace_id]);

  const deleteLocation = async () => {
    let Stage_id = 0;

    if (locationList[locationRowNo]) Stage_id = locationList[locationRowNo]?.Stage_id || 0;
    if (Stage_id) {
      setLoadingForConfirm(true);

      const workspace = new WorkspaceClass();
      const result = await workspace.deleteLocation(Workspace_id, Stage_id);

      if (result.success) {
        dispatch(
          showToast({
            message: result.message,
            type: "success",
          })
        );
        setLocationList((prev) => prev.filter((elem, index) => index !== locationRowNo));

        if (
          location === Stage_id //NOSONAR
        )
          setLocation("");
      }
      setOpenConfirm(false);
      setLoadingForConfirm(false);
    }
  };

  useEffect(() => {
    loadLocationList();
  }, [loadLocationList]);

  const validate = (str, setData) => {
    const isValid = regexName(str, "alphaWithUnderscore");
    if (isValid) setData(str);
  };

  const breadcrumbArr = [
    { link: "/my-workspaces", displayName: "Workspaces", active: false },
    { link: `/my-workspaces/${Workspace_id}`, displayName: workspaceName, active: false },
    { link: "#", displayName: "Data Connections", active: true },
  ];

  return (
    <CommonLayout title="Data Connections" breadcrumbArr={breadcrumbArr}>
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) => {
            return theme.palette.TiffanyBlue.main;
          },
          flexGrow: 1,
          height: "90vh",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        <Toolbar className={classes.Header}>
          <Box sx={{ display: "flex", flex: "1", alignItems: "center", minWidth: "340px", paddingRight: "16px" }}>
            <Typography className={classes.HeaderTitle} component="div" gutterBottom sx={{ paddingRight: "16px" }}>
              Data Connections
            </Typography>
            {[1, 2].includes(accessRole) ? (
              <ButtonUI
                endIcon={<AddIcon />}
                variant="contained"
                color="info"
                style={{ width: "auto" }}
                onClick={() => {
                  setSelectedConnection((prev) => ({
                    stage: 0,
                    url: "/",
                    type: "",
                    name: prev.name,
                  }));
                  setOpenLocationPopup(true);
                }}
              >
                Add Connection
              </ButtonUI>
            ) : (
              <></>
            )}
            <HelpContextIcon screen="Data Connections" />
          </Box>
        </Toolbar>

        <Box className={classes.PanelContainer}>
          <Box className={classes.DataTableContainer}>
            <KMKTable
              loading={tableLoading}
              columns={LocationColumns}
              containerHeight={tableHeight}
              sortingProperties={{}}
              noDataText={"No connection data available"}
              data={locationList}
              boldHeader
              tableProps={{ size: "medium", stickyHeader: true }}
              paginationProperties={{}}
              dataIdKey="Stage_id"
            />
          </Box>
        </Box>
      </Box>
      {openConfirm && (
        <ConfirmPopup
          configData={{
            popupTitle: confirmTitle,
            popupMessage: confirmMessage,
            handleCancel: handleCancel,
            handleClick: deleteLocation,
            loadingForConfirm: loadingForConfirm,
          }}
        />
      )}
      {openLocationPopup && (
        <AddLocation
          configData={{
            open: openLocationPopup,
            setOpen: setOpenLocationPopup,
            popupTitle: selectedConnection.stage ? "Edit Connection" : "Add Connection",
            alphaWithUnderscore: validate,
            locationList,
            setLocationList,
            Workspace_id,
            dataProviderList,
            loadLocationList,
            stage: selectedConnection.stage,
            accessRole,
          }}
        />
      )}
      {openFileBrowser && selectedConnection.type.toLowerCase() === "s3" && (
        <S3FileBrowser
          setOpenFileBrowser={setOpenFileBrowser}
          stage={selectedConnection.stage}
          locationName={selectedConnection.name}
          url={selectedConnection.url || "/"}
          hasUploadPermission={selectedConnection.hasUploadPermission || false}
        />
      )}
      {openFileBrowser && selectedConnection.type.toLowerCase() === "sftp" && (
        <SSHFileBrowser
          setOpenFileBrowser={setOpenFileBrowser}
          stage={selectedConnection.stage}
          locationName={selectedConnection.name}
          url={selectedConnection.url || "/"}
        />
      )}
      {openFileBrowser && selectedConnection.type.toLowerCase() === "sharepoint" && (
        <SharePointBrowser
          setOpenFileBrowser={setOpenFileBrowser}
          stage={selectedConnection.stage}
          locationName={selectedConnection.name}
          url={
            `${
              selectedConnection.url.endsWith("/")
                ? selectedConnection.url.substring(0, selectedConnection.url.length - 1)
                : selectedConnection.url
            }/${selectedConnection.docuList}` || "/"
          }
          docuList={selectedConnection.docuList}
          path={"/"}
        />
      )}
      {openFileBrowser && selectedConnection.type.toLowerCase() === "azure" && (
        <AzureFileBrowser
          setOpenFileBrowser={setOpenFileBrowser}
          stage={selectedConnection.stage}
          locationName={selectedConnection.name}
          path={`/${selectedConnection.container}/`}
          container={selectedConnection.container}
        />
      )}
    </CommonLayout>
  );
};

export default DataLocation;
